/**
 * The countries a deployed app can register organizations in, and how they are
 * named and ordered for the visitor.
 *
 * Mirrors `appTiers.ts`: the app profile carries codes, all human-readable copy
 * is resolved here, so signup and organization settings can only ever describe
 * the same set of countries.
 *
 * Country *names* come from `Intl.DisplayNames` rather than the translation
 * catalogs (change: international-signup, design D4). Enumerating a couple of
 * hundred country names in both English and Norwegian is copy that drifts and
 * that nobody proofreads; the platform already knows them, in every locale, for
 * free. A `countries.<code>` catalog entry still wins where one exists, so
 * specific wording can be overridden — `countries.no` predates this change.
 */
import { intlLocaleOf, type TranslateFn } from './appTiers';

/** One country as rendered in a picker. */
export interface CountryView {
    /** Lowercase ISO 3166-1 alpha-2 code — what the tenant id and API use. */
    code: string;
    /** Localized country name. */
    name: string;
}

/**
 * ISO 3166-1 alpha-2 codes an organization can be registered in.
 *
 * This is **data, not policy**. It exists so an app that is sold everywhere has
 * something to render in its picker. What a given app offers is its own
 * `signup.countries`; what the server accepts is the backend's per-app policy,
 * which is the only gate that counts — the signup state blob is unsigned, so the
 * picker is a convenience.
 *
 * Derived from the platform's own region list, minus groupings (`eu`, `un`),
 * pseudo-regions (`zz`), exceptional reservations (`uk` — `gb` is the ISO code)
 * and withdrawn codes (`su`, `yu`, `dd`). Every entry is nameable by
 * `Intl.DisplayNames` in both catalogs; `appCountries.test.ts` holds that true.
 */
export const ALL_REGISTRABLE_COUNTRIES: readonly string[] = [
    'ad', 'ae', 'af', 'ag', 'ai', 'al', 'am', 'ao', 'aq', 'ar', 'as', 'at',
    'au', 'aw', 'ax', 'az', 'ba', 'bb', 'bd', 'be', 'bf', 'bg', 'bh', 'bi',
    'bj', 'bl', 'bm', 'bn', 'bo', 'bq', 'br', 'bs', 'bt', 'bv', 'bw', 'by',
    'bz', 'ca', 'cc', 'cd', 'cf', 'cg', 'ch', 'ci', 'ck', 'cl', 'cm', 'cn',
    'co', 'cq', 'cr', 'cu', 'cv', 'cw', 'cx', 'cy', 'cz', 'de', 'dj', 'dk',
    'dm', 'do', 'dz', 'ec', 'ee', 'eg', 'eh', 'er', 'es', 'et', 'fi', 'fj',
    'fk', 'fm', 'fo', 'fr', 'ga', 'gb', 'gd', 'ge', 'gf', 'gg', 'gh', 'gi',
    'gl', 'gm', 'gn', 'gp', 'gq', 'gr', 'gs', 'gt', 'gu', 'gw', 'gy', 'hk',
    'hm', 'hn', 'hr', 'ht', 'hu', 'id', 'ie', 'il', 'im', 'in', 'io', 'iq',
    'ir', 'is', 'it', 'je', 'jm', 'jo', 'jp', 'ke', 'kg', 'kh', 'ki', 'km',
    'kn', 'kp', 'kr', 'kw', 'ky', 'kz', 'la', 'lb', 'lc', 'li', 'lk', 'lr',
    'ls', 'lt', 'lu', 'lv', 'ly', 'ma', 'mc', 'md', 'me', 'mf', 'mg', 'mh',
    'mk', 'ml', 'mm', 'mn', 'mo', 'mp', 'mq', 'mr', 'ms', 'mt', 'mu', 'mv',
    'mw', 'mx', 'my', 'mz', 'na', 'nc', 'ne', 'nf', 'ng', 'ni', 'nl', 'no',
    'np', 'nr', 'nu', 'nz', 'om', 'pa', 'pe', 'pf', 'pg', 'ph', 'pk', 'pl',
    'pm', 'pn', 'pr', 'ps', 'pt', 'pw', 'py', 'qa', 're', 'ro', 'rs', 'ru',
    'rw', 'sa', 'sb', 'sc', 'sd', 'se', 'sg', 'sh', 'si', 'sj', 'sk', 'sl',
    'sm', 'sn', 'so', 'sr', 'ss', 'st', 'sv', 'sx', 'sy', 'sz', 'tc', 'td',
    'tf', 'tg', 'th', 'tj', 'tk', 'tl', 'tm', 'tn', 'to', 'tr', 'tt', 'tv',
    'tw', 'tz', 'ua', 'ug', 'um', 'us', 'uy', 'uz', 'va', 'vc', 've', 'vg',
    'vi', 'vn', 'vu', 'wf', 'ws', 'xk', 'ye', 'yt', 'za', 'zm', 'zw',
];

/**
 * Countries whose company register we can look an organization up in.
 *
 * Norway only: Brønnøysundregistrene. This drives an **exclusive** choice, not a
 * default with an escape hatch — where a register exists it is the only way to
 * name an organization, including when the lookup fails or finds nothing. The
 * register is the authority on which Norwegian organizations exist, so a manual
 * fallback would quietly admit a name or number it does not agree with. Adding a
 * second register is an entry here plus a component.
 */
const COUNTRIES_WITH_COMPANY_REGISTER: ReadonlySet<string> = new Set(['no']);

/** Whether `code`'s organizations are found in a register rather than typed in. */
export const hasCompanyRegister = (code: string): boolean =>
    COUNTRIES_WITH_COMPANY_REGISTER.has(code.trim().toLowerCase());

/**
 * The country to start on, given the app's countries and the active locale.
 *
 * A Norwegian-speaking visitor lands on Norway and the register search — today's
 * journey, unchanged. Everyone else starts on the app's first country. Null only
 * when an app declares no countries at all, which the registry test forbids.
 */
export const defaultCountryFor = (
    countries: readonly string[],
    locale: string,
): string | null => {
    if (countries.length === 0) return null;
    const speaksNorwegian = locale === 'no' || locale === 'nb';
    if (speaksNorwegian && countries.includes('no')) return 'no';
    return countries[0];
};

/**
 * The localized name of a country.
 *
 * A `countries.<code>` catalog entry wins when present; otherwise
 * `Intl.DisplayNames`. Falls back to the upper-cased code when the platform
 * cannot name it — better a visible `ZZ` than an empty option.
 */
export const countryName = (
    code: string,
    translate: TranslateFn,
    locale: string,
): string => {
    const normalized = code.trim().toLowerCase();
    const key = `countries.${normalized}`;
    // Polyglot returns the key itself for a miss; `_` is its explicit fallback.
    const fromCatalog = translate(key, { _: '' });
    if (fromCatalog && fromCatalog !== key) return fromCatalog;

    try {
        // `Intl.DisplayNames` expects the region code upper-cased; a lowercase
        // argument yields nothing at all, which is how the picker would have
        // ended up as a column of two-letter codes.
        const display = new Intl.DisplayNames([intlLocaleOf(locale)], {
            type: 'region',
            fallback: 'none',
        });
        return display.of(normalized.toUpperCase()) ?? normalized.toUpperCase();
    } catch {
        return normalized.toUpperCase();
    }
};

/**
 * The app's countries as pickable options, sorted by localized name so the list
 * reads correctly in the language it is presented in.
 */
export const buildCountryViews = (
    countries: readonly string[],
    translate: TranslateFn,
    locale: string,
): CountryView[] => {
    const collator = new Intl.Collator(intlLocaleOf(locale));
    return countries
        .map(code => ({ code, name: countryName(code, translate, locale) }))
        .sort((a, b) => collator.compare(a.name, b.name));
};
